import type { JSX } from 'react'
import { Link, useLocation } from 'react-router-dom'

import {
  CheckIcon,
  DropdownMenu,
  ExpandIcon,
  IconButton,
  PencilIcon,
  XIcon,
} from '@buffer-mono/popcorn'

import { MoreActionsDropdown } from '~publish/components/PostCard/PostCardActions/MoreActionsDropdown'
import { DeletePostMenuItem } from '~publish/components/PostCard/PostCardActions/DeletePostMenuItem'
import {
  DuplicateMenuItem,
  useApprovePostAction,
  useRejectPostAction,
  ViewPostMenuItem,
} from '~publish/components/PostCard/PostCardActions/PostCardActions'
import { usePostData } from '~publish/components/PostCard/PostCardContext'
import { usePostComposer } from '~publish/hooks/usePostComposer'
import { canPerform } from '~publish/utils/permissions/canPerform'

const EditMenuItem = (): JSX.Element | null => {
  const { allowedActions, id, postGroup } = usePostData()
  const { editPostInComposer } = usePostComposer()

  if (!allowedActions.includes('updatePost')) {
    return null
  }

  return (
    <DropdownMenu.Item
      onSelect={(): Promise<void> =>
        editPostInComposer({
          postId: id,
          cta: 'publish-board-postCard-editPost-1',
          contentItemId: postGroup?.id,
          contentItemTitle: postGroup?.title || null,
        })
      }
    >
      <PencilIcon />
      Edit
    </DropdownMenu.Item>
  )
}

const ApproveMenuItem = (): JSX.Element | null => {
  const { allowedActions } = usePostData()
  const { approve, label } = useApprovePostAction()

  if (!allowedActions.includes('approvePost')) {
    return null
  }

  return (
    <DropdownMenu.Item onSelect={approve}>
      <CheckIcon />
      {label}
    </DropdownMenu.Item>
  )
}

const RejectMenuItem = (): JSX.Element | null => {
  const { allowedActions } = usePostData()
  const { reject } = useRejectPostAction()

  if (!allowedActions.includes('rejectPost')) {
    return null
  }

  return (
    <DropdownMenu.Item variant="critical" onSelect={reject}>
      <XIcon />
      Reject
    </DropdownMenu.Item>
  )
}

const PostDetailsIconButton = (): JSX.Element => {
  const { id } = usePostData()
  const { search } = useLocation()

  return (
    <IconButton
      as={Link}
      to={`posts/${id}${search}`}
      label="Post Details"
      tooltip="Post Details"
      aria-haspopup="dialog"
      aria-expanded="false"
    >
      <ExpandIcon />
    </IconButton>
  )
}

/**
 * Board's card is a glance-card, not the full footer's status-specific
 * action set — a deliberately smaller, curated menu (mirroring Ideas'
 * board card), not full parity with `PostCardActions`.
 *
 * When Post Details is the only item a member would get, the card opens it
 * directly instead of through a one-item menu.
 */
export function BoardPostCardActions(): JSX.Element | null {
  const { allowedActions, channel } = usePostData()
  // Mirrors the gates of the menu items below; `manageUpdates` is the gate
  // that decides whether `DuplicateMenuItem` renders.
  const hasMenuActions =
    allowedActions.includes('approvePost') ||
    allowedActions.includes('rejectPost') ||
    allowedActions.includes('updatePost') ||
    allowedActions.includes('deletePost') ||
    canPerform(channel, 'manageUpdates')

  if (!hasMenuActions) {
    return <PostDetailsIconButton />
  }

  return (
    <MoreActionsDropdown>
      <MoreActionsDropdown.Group>
        <ApproveMenuItem />
        <RejectMenuItem />
      </MoreActionsDropdown.Group>
      <MoreActionsDropdown.Group>
        <EditMenuItem />
        <DuplicateMenuItem />
        <ViewPostMenuItem />
      </MoreActionsDropdown.Group>
      <MoreActionsDropdown.Group>
        <DeletePostMenuItem />
      </MoreActionsDropdown.Group>
    </MoreActionsDropdown>
  )
}
